/**
 * Regra pura do pipeline comercial (Task 13, brief 3.4 / D-13.3).
 * **Leia isto antes de mexer — é o coração do estágio comercial.**
 *
 * Endereço CANÔNICO (Task 13, limpeza de arquitetura): regra pura promovida de
 * `apps/plataforma/src/crm/pipeline.ts` para o núcleo compartilhado
 * `packages/crm`, conforme o handoff H-13.A e o ADR-0004/ADR-0007. A Edge
 * Function `supabase/functions/crm-runtime` e o app `apps/plataforma` IMPORTAM
 * daqui; o arquivo antigo em `apps/plataforma/src/crm/pipeline.ts` passou a
 * reexportar deste módulo.
 *
 * Invariantes deste módulo:
 *
 * 1. **Puro e sem I/O.** Nada aqui faz fetch, lê env, toca DOM ou banco. É por
 *    isso que a MESMA função valida a mudança de estágio no servidor
 *    (`apps/api-dev` hoje; Edge Function depois) e decide o que a tela oferece.
 *    Duas implementações dariam duas negociações diferentes para o mesmo
 *    registro — o mesmo erro que a Task 12 evitou na máquina de pedidos.
 * 2. **O vocabulário de estágio não é novo.** `LeadStage` é o MESMO contrato
 *    que `packages/ui` define desde a Fase 1 e que a tela de cliente consome.
 *    Aqui ele é derivado de `LEAD_STAGES` (a lista única deste módulo); um
 *    teste (`tests/plataforma-crm.test.ts`) compara, por texto, esta lista com
 *    `export type LeadStage` de `packages/ui`, de modo que os dois lados NÃO
 *    divergem. Não importamos o tipo de `packages/ui` por caminho relativo
 *    porque este pacote é EMITIDO para o Deno (ADR-0004) e importar a fonte
 *    `.ts` de `packages/ui` forçaria a emissão transitória daquele pacote
 *    `.ts`-only — o que a limpeza da Task 13 reverteu de propósito. Os valores
 *    são exatamente os `check (stage in (...))` de `crm_opportunities` (0032).
 * 3. **Fechar negociação exige a prova do fechamento.** `ganho` sem o pedido
 *    que a concretizou e `perdido` sem motivo não são transições incompletas:
 *    são transições INVÁLIDAS. Oportunidade marcada como ganha sem venda real
 *    infla o funil e é exatamente o número cego que a fase combate.
 * 4. **Orçamento não reprecifica.** O total da proposta é somado dos itens com
 *    o preço travado na emissão; a margem usa o custo copiado no item. Sem
 *    custo, a margem é declarada indisponível — nunca estimada.
 */
/**
 * Estágios do funil, em ordem de avanço. `ganho`/`perdido` são as duas saídas.
 * Mesma lista do `check` da 0032 e do tipo `LeadStage` de `packages/ui`
 * (igualdade verificada por teste de texto).
 */
export const LEAD_STAGES = ["novo", "qualificando", "proposta", "negociacao", "ganho", "perdido"];
export const LEAD_STAGE_LABELS = {
    novo: "Novo",
    qualificando: "Qualificando",
    proposta: "Proposta enviada",
    negociacao: "Em negociação",
    ganho: "Ganho (venda registrada)",
    perdido: "Perdido"
};
/** Canal onde a oportunidade nasceu — mesmo vocabulário de `platform_orders`. */
export const OPPORTUNITY_SOURCES = ["loja", "whatsapp", "marketplace", "manual"];
/** Rótulo exibido ao operador. WhatsApp aparece como "WAPI da TDZAIN" (D-00). */
export const OPPORTUNITY_SOURCE_LABELS = {
    loja: "Loja (checkout do site)",
    whatsapp: "WhatsApp (WAPI da TDZAIN)",
    marketplace: "Marketplace",
    manual: "Prospecção/venda direta"
};
/**
 * Transições permitidas entre estágios.
 *
 * Escolhas que merecem explicação (D-13.6):
 * - **o funil anda para frente e pode voltar um passo.** Negociação que esfria
 *   volta para `qualificando`; o que não existe é pular de `novo` para
 *   `negociacao` sem ter havido proposta, porque isso apagaria a etapa em que a
 *   proposta teria sido feita (e o orçamento que a sustenta);
 * - **`perdido` sai de qualquer estágio aberto.** Perder é possível a qualquer
 *   momento, e esconder isso empurraria o operador a deixar a oportunidade
 *   aberta para sempre;
 * - **`ganho` sai só de `negociacao`.** A venda fechada vem depois de uma
 *   proposta negociada; marcar ganho direto de `novo` seria registrar uma
 *   venda sem rastro da negociação que a produziu;
 * - **`ganho` e `perdido` são finais.** Nova demanda do mesmo cliente é uma
 *   oportunidade NOVA, com o seu próprio histórico — reabrir sobrescreveria o
 *   resultado de uma negociação já contada no funil.
 */
export const ALLOWED_STAGE_TRANSITIONS = {
    novo: ["qualificando", "perdido"],
    qualificando: ["proposta", "novo", "perdido"],
    proposta: ["negociacao", "qualificando", "perdido"],
    negociacao: ["ganho", "proposta", "perdido"],
    ganho: [],
    perdido: []
};
/** Estágios de saída: a oportunidade não está mais em aberto. */
export const CLOSED_STAGES = ["ganho", "perdido"];
export function isLeadStage(value) {
    return typeof value === "string" && LEAD_STAGES.includes(value);
}
export function isOpportunitySource(value) {
    return typeof value === "string" && OPPORTUNITY_SOURCES.includes(value);
}
export function isClosedStage(stage) {
    return CLOSED_STAGES.includes(stage);
}
export function nextStages(stage) {
    return ALLOWED_STAGE_TRANSITIONS[stage];
}
export function canAdvanceStage(from, to) {
    return ALLOWED_STAGE_TRANSITIONS[from].includes(to);
}
/** Campos obrigatórios por estágio de destino. Estágio ausente = nada exigido. */
export const STAGE_TRANSITION_REQUIREMENTS = {
    perdido: ["lostReason"],
    ganho: ["platformOrderId"]
};
export const STAGE_FIELD_LABELS = {
    lostReason: "Motivo da perda",
    platformOrderId: "Pedido que concretizou a venda"
};
export function stageRequirementsFor(to) {
    return STAGE_TRANSITION_REQUIREMENTS[to] ?? [];
}
/** Campos exigidos pela transição de estágio que não vieram preenchidos. */
export function missingStageRequirements(to, input = {}) {
    return stageRequirementsFor(to).filter((field) => {
        const value = input[field];
        return typeof value !== "string" || value.trim() === "";
    });
}
/**
 * Única porta de validação da mudança de estágio — usada pelo servidor (antes
 * de gravar) e pela tela (antes de oferecer). Devolve o motivo da recusa em vez
 * de lançar: quem chama precisa dizer ao operador O QUE falta.
 */
export function checkStageTransition(from, to, input = {}) {
    if (!isLeadStage(to))
        return { ok: false, error: "unknown_stage", fields: ["to"] };
    if (!canAdvanceStage(from, to))
        return { ok: false, error: "invalid_stage_transition", fields: [from, to] };
    const missing = missingStageRequirements(to, input);
    if (missing.length)
        return { ok: false, error: "stage_requirements_missing", fields: missing };
    return { ok: true };
}
// ---------------------------------------------------------------------------
// Orçamento (proposta comercial)
// ---------------------------------------------------------------------------
/** Situação da proposta (`check (status in (...))` de `crm_quotes`). */
export const QUOTE_STATUSES = ["draft", "sent", "accepted", "rejected", "expired"];
export const QUOTE_STATUS_LABELS = {
    draft: "Rascunho",
    sent: "Enviado ao cliente",
    accepted: "Aceito",
    rejected: "Recusado",
    expired: "Vencido"
};
export function isQuoteStatus(value) {
    return typeof value === "string" && QUOTE_STATUSES.includes(value);
}
/**
 * Totais e margem da proposta a partir dos itens, do desconto e do frete.
 *
 * Nenhum valor é reprecificado: `unitPriceCents` é o preço travado na emissão.
 * O frete entra no total quando cotado e fica fora da margem (margem é dos
 * itens; frete repassado não é lucro de produto) — a mesma separação que
 * `packages/pricing` usa ao recomendar preço.
 */
export function quoteTotals(items, options = {}) {
    const discountCents = Math.max(0, options.discountCents ?? 0);
    const shippingCents = options.shippingCents ?? null;
    const subtotalCents = items.reduce((sum, item) => sum + item.unitPriceCents * item.quantity, 0);
    const hasAllCosts = items.length > 0 && items.every((item) => item.unitCostCents !== null);
    const costCents = hasAllCosts
        ? items.reduce((sum, item) => sum + item.unitCostCents * item.quantity, 0)
        : null;
    const netCents = Math.max(0, subtotalCents - discountCents);
    const marginPct = costCents === null || netCents === 0 ? null : Math.round(((netCents - costCents) / netCents) * 10000) / 100;
    return {
        subtotalCents,
        discountCents,
        shippingCents,
        totalCents: shippingCents === null ? null : netCents + shippingCents,
        costCents,
        marginPct
    };
}
/**
 * A proposta está vencida neste instante? `validUntil` ausente = sem prazo
 * declarado, então não vencemos nada por conta própria.
 */
export function isQuoteExpired(quote, now) {
    if (quote.status === "accepted" || quote.status === "rejected")
        return false;
    if (!quote.validUntil)
        return false;
    const deadline = new Date(quote.validUntil).getTime();
    if (Number.isNaN(deadline))
        return false;
    return deadline < now.getTime();
}
/**
 * Situação EFETIVA da proposta: `expired` é derivado do prazo em vez de
 * depender de uma rotina que carimba a coluna. Proposta vencida exibida como
 * "enviada" faria o operador cobrar uma resposta de um preço que não vale mais.
 */
export function effectiveQuoteStatus(quote, now) {
    return isQuoteExpired(quote, now) ? "expired" : quote.status;
}
// ---------------------------------------------------------------------------
// Tarefas e follow-ups
// ---------------------------------------------------------------------------
export const TASK_KINDS = ["follow_up", "call", "whatsapp", "email", "visit", "quote_review"];
export const TASK_KIND_LABELS = {
    follow_up: "Follow-up",
    call: "Ligação",
    whatsapp: "Mensagem no WhatsApp (WAPI da TDZAIN)",
    email: "E-mail",
    visit: "Visita",
    quote_review: "Revisão de orçamento"
};
export const TASK_STATUSES = ["open", "done", "cancelled"];
export function isTaskKind(value) {
    return typeof value === "string" && TASK_KINDS.includes(value);
}
export function isTaskStatus(value) {
    return typeof value === "string" && TASK_STATUSES.includes(value);
}
/**
 * Situação do prazo da tarefa. Tarefa concluída/cancelada não é "atrasada":
 * ela saiu da fila, e marcá-la como atrasada inflaria o alerta da operação.
 */
export function taskDueState(task, now) {
    if (task.status !== "open")
        return "closed";
    const due = new Date(task.dueAt).getTime();
    if (Number.isNaN(due))
        return "upcoming";
    if (due < now.getTime())
        return "overdue";
    // "Hoje" é o mesmo dia civil do prazo, no fuso de quem opera: a fila do dia
    // precisa mostrar o que vence hoje mesmo que o horário já tenha passado de
    // manhã (esses caem em `overdue` acima, que é o aviso correto).
    const dueDate = new Date(due);
    const sameDay = dueDate.getFullYear() === now.getFullYear() &&
        dueDate.getMonth() === now.getMonth() &&
        dueDate.getDate() === now.getDate();
    return sameDay ? "today" : "upcoming";
}
